import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Btn } from "./ui";
import { grantsArea } from "../lib/sections";
import { useCampaign } from "../lib/campaign";
import { CampaignElection, ELECTION_CHANGED, electionDay, electionInSentence, loadElection } from "../lib/election";

// ── AFTER AN ELECTION: ONE QUESTION (0415) ──────────────────────────────────────────────────────
//
// Caleb, 2026-10-31: "The banner seems right but I would be inclined to redirect them into the
// settings where they have an opportunity to see and maybe adjust." So the banner only ASKS: either
// answer opens Settings -> Election with that answer filled in and what it would change, and nothing
// changes until somebody presses Confirm there. Managers only, at the top of every page, until one
// manager has answered for the campaign. Nothing is blocked, and nobody has to answer: the campaign
// moves on by itself once the next election day has passed.
//
// A failure to load says nothing. This is a prompt, not a page: an error here would sit on top of
// every screen of the dashboard for a question the campaign is not obliged to answer.
export function ElectionBanner() {
  const { current } = useCampaign();
  const loc = useLocation();
  const nav = useNavigate();
  const [data, setData] = useState<CampaignElection | null>(null);
  const may = !!current && grantsArea(current, "account");

  useEffect(() => {
    if (!current || !may) { setData(null); return; }
    let dead = false;
    const load = () => loadElection(current.id).then((r) => { if (!dead) setData(r); }).catch(() => { if (!dead) setData(null); });
    load();
    window.addEventListener(ELECTION_CHANGED, load);
    return () => { dead = true; window.removeEventListener(ELECTION_CHANGED, load); };
  }, [current?.id, may]);

  const b = data?.banner;
  // Settings is where the answer is given; the question does not sit on top of its own answer.
  if (!b || !data?.can_manage || loc.pathname.startsWith("/settings")) return null;
  const go = (id: string, kind?: string) => nav(`/settings?election=${encodeURIComponent(id)}${kind ? `&kind=${kind}` : ""}`);
  const over = `${electionInSentence(b.prev).replace(/^the /, "The ")} is over.`;

  return (
    <div className="mx-4 mt-3 rounded-lg border border-accent bg-accent/10 px-3 py-2 flex items-center gap-3 flex-wrap text-sm">
      <span aria-hidden="true">🗳</span>
      {b.ask_runoff ? (
        <>
          <span className="font-semibold">
            {over} {b.yes ? <>Are you in the <b>{electionDay(b.yes.day, { year: false })} runoff</b>?</> : <>Are you in a runoff?</>}
          </span>
          <span className="ml-auto flex gap-2">
            <Btn onClick={() => (b.yes ? go(b.yes.id) : go("new", "local_runoff"))}>{b.yes ? "Yes, the runoff" : "Yes, a runoff"}</Btn>
            <Btn variant="ghost" onClick={() => (b.no ? go(b.no.id) : nav("/settings"))}>
              {b.no ? `No, on to ${electionDay(b.no.day, { year: false })}` : "No"}
            </Btn>
          </span>
        </>
      ) : (
        <>
          <span className="font-semibold">
            {over} {b.next ? <>Your campaign is now in {electionInSentence(b.next)}.</> : <>There's no election ahead on your path yet.</>}
          </span>
          <span className="ml-auto"><Btn variant="ghost" onClick={() => (b.next ? go(b.next.id) : nav("/settings"))}>Review in Settings</Btn></span>
        </>
      )}
    </div>
  );
}
